import React, { useState, useEffect } from 'react';
import { X, Download, RefreshCw, Search, AlertCircle, ChevronDown, ChevronRight, Activity } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { api } from '../../lib/api';

interface LogsViewerDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface LogEvent {
  timestamp: number;
  message: string;
  logStreamName?: string;
  requestId?: string;
}

interface LogGroup {
  requestId: string;
  startTime: number;
  endTime: number;
  logs: LogEvent[];
  expanded: boolean;
}

interface LogsResponse {
  logs: LogEvent[];
  count: number;
  uniqueRequests?: number;
  pagesRetrieved?: number;
  startTime: string;
  endTime: string;
  environment: string;
  hours?: number;
}

interface ExecutionEvent {
  id?: number;
  type?: string;
  timestamp?: string;
  eventTimestamp?: string;
  [key: string]: unknown;
}

interface ExecutionHistoryResponse {
  execution?: {
    executionArn?: string;
    status?: string;
    startDate?: string;
    stopDate?: string;
    input?: string;
    output?: string;
  };
  events?: ExecutionEvent[];
  environment?: string;
  nextToken?: string;
}

type LogType = 'trader' | 'executions';

export const LogsViewerDialog: React.FC<LogsViewerDialogProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<LogType>('trader');

  // Separate state for each tab
  const [traderLogs, setTraderLogs] = useState<LogEvent[]>([]);
  const [traderLogGroups, setTraderLogGroups] = useState<LogGroup[]>([]);
  const [traderFilteredGroups, setTraderFilteredGroups] = useState<LogGroup[]>([]);
  const [traderMetadata, setTraderMetadata] = useState<Omit<LogsResponse, 'logs'> | null>(null);

  const [executionResponse, setExecutionResponse] = useState<ExecutionHistoryResponse | null>(null);
  const [executionError, setExecutionError] = useState<string | null>(null);
  const [executionLoading, setExecutionLoading] = useState(false);
  const [executionQuery, setExecutionQuery] = useState({ correlationId: '', executionArn: '' });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Get current tab's data

  useEffect(() => {
    if (isOpen && activeTab === 'trader') {
      fetchLogs('trader');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (activeTab !== 'trader') {
      return;
    }

    if (searchTerm) {
      const filtered = traderLogGroups.filter(group =>
        group.logs.some(log => log.message.toLowerCase().includes(searchTerm.toLowerCase()))
      ).map(group => ({
        ...group,
        logs: group.logs.filter(log =>
          log.message.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }));
      setTraderFilteredGroups(filtered);
    } else {
      setTraderFilteredGroups(traderLogGroups);
    }
  }, [searchTerm, traderLogGroups, activeTab]);

  const extractRequestId = (message: string): string | undefined => {
    // Try to extract RequestId from various log formats
    const patterns = [
      /RequestId:\s*([a-f0-9-]+)/i,
      /START RequestId:\s*([a-f0-9-]+)/,
      /END RequestId:\s*([a-f0-9-]+)/,
      /REPORT RequestId:\s*([a-f0-9-]+)/,
      // Also try to extract from the log timestamp format
      /^[0-9-T:.Z]+\s+([a-f0-9-]+)\s+/,
      // CloudWatch format: "2024-01-01T12:00:00.000Z [request-id] message"
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z\s+([a-f0-9-]+)\s+/
    ];

    for (const pattern of patterns) {
      const match = message.match(pattern);
      if (match) return match[1];
    }

    return undefined;
  };

  const groupLogsByRequestId = (logEvents: LogEvent[]): LogGroup[] => {
    const groups: { [key: string]: LogGroup } = {};

    logEvents.forEach(log => {
      const requestId = extractRequestId(log.message) || log.requestId || 'ungrouped';

      if (!groups[requestId]) {
        groups[requestId] = {
          requestId,
          startTime: log.timestamp,
          endTime: log.timestamp,
          logs: [],
          expanded: false
        };
      }

      groups[requestId].logs.push(log);
      groups[requestId].endTime = Math.max(groups[requestId].endTime, log.timestamp);
      groups[requestId].startTime = Math.min(groups[requestId].startTime, log.timestamp);
    });

    // Sort logs within each group by timestamp (ascending order)
    Object.values(groups).forEach(group => {
      group.logs.sort((a, b) => a.timestamp - b.timestamp);
    });

    // Sort groups by start time (newest first)
    return Object.values(groups).sort((a, b) => b.startTime - a.startTime);
  };

  const fetchLogs = async (logType: LogType) => {
    setLoading(true);
    setError(null);
    try {
      if (logType !== 'trader') {
        return;
      }

      const response = await api.get<LogsResponse>('/logs', {
        params: { logType }
      });

      const enhancedLogs = response.data.logs.map(log => ({
        ...log,
        requestId: extractRequestId(log.message)
      }));

      const groupsWithExpansion = groupLogsByRequestId(enhancedLogs).map(group => ({
        ...group,
        expanded: false
      }));

      setTraderLogs(enhancedLogs);
      setTraderLogGroups(groupsWithExpansion);
      setTraderFilteredGroups(groupsWithExpansion);
      setTraderMetadata({
        count: response.data.count,
        startTime: response.data.startTime,
        endTime: response.data.endTime,
        environment: response.data.environment,
        hours: response.data.hours,
        uniqueRequests: response.data.uniqueRequests,
        pagesRetrieved: response.data.pagesRetrieved
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (requestId: string) => {
    if (activeTab !== 'trader') {
      return;
    }

    setTraderLogGroups(prev => prev.map(group =>
      group.requestId === requestId
        ? { ...group, expanded: !group.expanded }
        : group
    ));
    setTraderFilteredGroups(prev => prev.map(group =>
      group.requestId === requestId
        ? { ...group, expanded: !group.expanded }
        : group
    ));
  };

  const expandCollapseAll = () => {
    if (activeTab !== 'trader') {
      return;
    }

    const allExpanded = traderLogGroups.every(g => g.expanded);
    setTraderLogGroups(prev => prev.map(group => ({ ...group, expanded: !allExpanded })));
    setTraderFilteredGroups(prev => prev.map(group => ({ ...group, expanded: !allExpanded })));
  };

  const downloadLogs = () => {
    if (activeTab !== 'trader') {
      if (executionResponse?.events?.length) {
        const content = executionResponse.events
          .map((event) => `[${event.timestamp ?? event.eventTimestamp ?? ''}] [${event.type ?? 'Event'}] ${JSON.stringify(event)}`)
          .join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `workflow-execution-${new Date().toISOString()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      return;
    }

    const content = traderFilteredGroups.flatMap(group =>
      group.logs.map(log =>
        `[${new Date(log.timestamp).toISOString()}] [${group.requestId}] ${log.message}`
      )
    ).join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trading-api-logs-${new Date().toISOString()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const fetchExecutionHistory = async () => {
    const trimmedCorrelation = executionQuery.correlationId.trim();
    const trimmedArn = executionQuery.executionArn.trim();

    if (!trimmedArn && !trimmedCorrelation) {
      setExecutionError('Provide a correlation ID or execution ARN');
      return;
    }

    setExecutionLoading(true);
    setExecutionError(null);

    try {
      const params = trimmedArn
        ? { executionArn: trimmedArn }
        : { correlationId: trimmedCorrelation };

      const response = await api.get<ExecutionHistoryResponse>('/executions', { params });
      setExecutionResponse(response.data);
    } catch (err) {
      console.error('Failed to fetch execution history', err);
      setExecutionError(err instanceof Error ? err.message : 'Failed to fetch execution history');
      setExecutionResponse(null);
    } finally {
      setExecutionLoading(false);
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const highlightSearchTerm = (text: string) => {
    if (!searchTerm) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase()
        ? <mark key={index} className="bg-yellow-200">{part}</mark>
        : part
    );
  };

  const getLogLevelColor = (message: string) => {
    if (message.includes('ERROR') || message.includes('❌')) return 'text-red-600';
    if (message.includes('WARN')) return 'text-yellow-600';
    if (message.includes('✅')) return 'text-green-600';
    if (message.includes('===')) return 'text-blue-600 font-semibold';
    return 'text-gray-700';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Debug Logs</h2>
            {activeTab === 'trader' && traderMetadata && (
              <div className="text-sm text-gray-500 mt-1">
                <p>Last {traderMetadata.hours || 24} hours • {traderMetadata.count} events • {traderMetadata.uniqueRequests || traderLogGroups.length} unique requests</p>
                <p className="text-xs">Environment: {traderMetadata.environment} • Retrieved {traderMetadata.pagesRetrieved || 1} page(s)</p>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as LogType)} className="flex-1 flex flex-col min-h-0">
          <div className="px-4 pt-4 flex-shrink-0">
            <TabsList className="grid w-full max-w-xl grid-cols-2">
              <TabsTrigger value="trader" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Trading API
              </TabsTrigger>
              <TabsTrigger value="executions" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Step Functions
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Toolbar */}
          <div className="p-4 border-b border-gray-200 flex items-center gap-4 flex-shrink-0">
            {activeTab === 'trader' ? (
              <>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={expandCollapseAll}
                  disabled={loading || traderLogGroups.length === 0}
                >
                  {traderLogGroups.every(g => g.expanded) ? 'Collapse All' : 'Expand All'}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchLogs('trader')}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={downloadLogs}
                  disabled={loading || traderFilteredGroups.length === 0}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-3 w-full">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    placeholder="Correlation ID"
                    value={executionQuery.correlationId}
                    onChange={(e) => setExecutionQuery(prev => ({ ...prev, correlationId: e.target.value }))}
                  />
                  <Input
                    placeholder="Execution ARN"
                    value={executionQuery.executionArn}
                    onChange={(e) => setExecutionQuery(prev => ({ ...prev, executionArn: e.target.value }))}
                  />
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={fetchExecutionHistory}
                  disabled={executionLoading}
                >
                  {executionLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Fetching
                    </>
                  ) : (
                    'Fetch Execution'
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={downloadLogs}
                  disabled={executionLoading || !executionResponse?.events?.length}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            )}
          </div>

          <TabsContent value="trader" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
            {loading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Error loading logs</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}

            {!loading && !error && traderFilteredGroups.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? 'No logs match your search' : 'No trading API logs found in the selected window'}
              </div>
            )}

            {!loading && !error && traderFilteredGroups.length > 0 && (
              <div className="space-y-3">
                {traderFilteredGroups.map((group) => (
                  <div
                    key={group.requestId}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Group Header */}
                    <div
                      className={`px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-100 ${
                        group.logs.some(log => log.message.includes('ERROR') || log.message.includes('❌'))
                          ? 'bg-red-50'
                          : 'bg-gray-50'
                      }`}
                      onClick={() => toggleGroup(group.requestId)}
                    >
                      <div className="flex items-center gap-2">
                        {group.expanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <span className="font-mono text-xs font-medium text-gray-700">
                          {group.requestId === 'ungrouped' ? 'Ungrouped Logs' : `Request: ${group.requestId}`}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({group.logs.length} logs)
                        </span>
                        {group.logs.some(log => log.message.includes('ERROR') || log.message.includes('❌')) && (
                          <span className="text-xs text-red-600 font-semibold">ERROR</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatTimestamp(group.startTime)}
                        {group.startTime !== group.endTime && (
                          <> - {formatTimestamp(group.endTime)}</>
                        )}
                      </div>
                    </div>

                    {/* Group Logs */}
                    {group.expanded && (
                      <div className="bg-white p-2 space-y-1">
                        {group.logs.map((log, index) => (
                          <div
                            key={`${group.requestId}-${index}`}
                            className="font-mono text-[10px] leading-relaxed py-1 hover:bg-gray-50 px-2 rounded"
                          >
                            <div className="flex items-start gap-2">
                              <span className="text-gray-400 flex-shrink-0 text-[10px]">
                                {new Date(log.timestamp).toLocaleTimeString()}
                              </span>
                              <pre className={`whitespace-pre-wrap break-all flex-1 ${getLogLevelColor(log.message)} text-[10px]`}>
                                {highlightSearchTerm(log.message)}
                              </pre>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="executions" className="flex-1 min-h-0 overflow-y-auto p-4 mt-0">
            {executionLoading && (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {executionError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start mb-4">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">Execution lookup failed</h3>
                  <p className="text-sm text-red-700 mt-1">{executionError}</p>
                </div>
              </div>
            )}

            {!executionLoading && !executionError && !executionResponse && (
              <div className="text-center py-10 text-gray-500 text-sm">
                Enter a correlation ID or execution ARN to view workflow history.
              </div>
            )}

            {executionResponse && (
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">Execution Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600">
                    <div><span className="font-medium text-gray-700">Status:</span> {executionResponse.execution?.status}</div>
                    <div><span className="font-medium text-gray-700">Execution ARN:</span> <code className="break-all">{executionResponse.execution?.executionArn}</code></div>
                    <div><span className="font-medium text-gray-700">Started:</span> {executionResponse.execution?.startDate ? new Date(executionResponse.execution.startDate).toLocaleString() : 'n/a'}</div>
                    <div><span className="font-medium text-gray-700">Stopped:</span> {executionResponse.execution?.stopDate ? new Date(executionResponse.execution.stopDate).toLocaleString() : 'running'}</div>
                    {executionResponse.execution?.input && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700">Input:</span>
                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 mt-1 whitespace-pre-wrap break-words">
                          {executionResponse.execution.input}
                        </pre>
                      </div>
                    )}
                    {executionResponse.execution?.output && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700">Output:</span>
                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 mt-1 whitespace-pre-wrap break-words">
                          {executionResponse.execution.output}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">Timeline</h3>
                  {executionResponse.events && executionResponse.events.length > 0 ? (
                    <div className="space-y-2">
                      {executionResponse.events.map((event: ExecutionEvent) => (
                        <div key={event.id} className="border border-gray-100 rounded-md p-3 text-xs bg-gray-50">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-700">{event.type}</span>
                            <span className="text-gray-500">{event.timestamp ? new Date(event.timestamp).toLocaleString() : event.eventTimestamp ? new Date(event.eventTimestamp).toLocaleString() : ''}</span>
                          </div>
                          <pre className="mt-2 bg-white border border-gray-200 rounded p-2 whitespace-pre-wrap break-words">
                            {JSON.stringify(event, null, 2)}
                          </pre>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 text-xs">No execution events available.</div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
          <div className="text-sm text-gray-500">
            {activeTab === 'trader'
              ? `${traderFilteredGroups.length} groups, ${traderFilteredGroups.reduce((sum, g) => sum + g.logs.length, 0)} of ${traderLogs.length} logs shown`
              : executionResponse?.events
                ? `${executionResponse.events.length} workflow events retrieved`
                : 'Awaiting execution selection'}
          </div>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};
